import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';

const DataCacheContext = createContext(null);

const STALE_TIME_MS = 300 * 1000; // 300s
const PERSIST_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const STORAGE_PREFIX = 'fvs:dataCache:v1';

const PERSIST_PREFIXES = [
  'inventoryPieces:',
  'bundles:',
  'fabricSpecs:',
];

function safeJsonParse(text) {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (_) {
    return null;
  }
}

async function safeReadJson(response, { context = '' } = {}) {
  if (!response) return null;
  if (response.status === 204) return null;
  const contentType = response.headers?.get?.('content-type') || '';
  const text = await response.text();
  const trimmed = String(text || '').trim();

  // Empty body is treated as "no data" (some endpoints may respond 200 with no body).
  if (!trimmed) return {};

  const isJson = contentType.includes('application/json') || contentType.includes('+json');
  if (!isJson) {
    const preview = trimmed.slice(0, 300);
    const err = new Error(
      `Server returned non-JSON response${context ? ` for ${context}` : ''} (status ${response.status}).`
    );
    err.status = response.status;
    err.preview = preview;
    throw err;
  }

  const parsed = safeJsonParse(trimmed);
  if (parsed === null) {
    const preview = trimmed.slice(0, 300);
    const err = new Error(
      `Server returned invalid JSON${context ? ` for ${context}` : ''} (status ${response.status}).`
    );
    err.status = response.status;
    err.preview = preview;
    throw err;
  }

  return parsed;
}


export function DataCacheProvider({ children }) {
  const { currentUser } = useAuth();
  const cacheRef = useRef(new Map()); // key -> { value, fetchedAtMs }

  const getStorageKey = useCallback(
    (key) => {
      const uid = currentUser?.uid;
      if (!uid || !key) return null;
      return `${STORAGE_PREFIX}:${uid}:${key}`;
    },
    [currentUser]
  );

  const isPersistableKey = useCallback((key) => {
    const s = String(key || '');
    return PERSIST_PREFIXES.some((p) => s.startsWith(p));
  }, []);

  const tryHydrateFromStorage = useCallback(
    (key) => {
      if (!isPersistableKey(key)) return null;
      const storageKey = getStorageKey(key);
      if (!storageKey) return null;
      if (typeof localStorage === 'undefined') return null;
      try {
        const raw = localStorage.getItem(storageKey);
        const parsed = safeJsonParse(raw);
        const fetchedAtMs = Number(parsed?.fetchedAtMs);
        if (!parsed || !parsed.value || !Number.isFinite(fetchedAtMs)) return null;
        const ageMs = Date.now() - fetchedAtMs;
        if (ageMs < 0 || ageMs > PERSIST_TTL_MS) return null;
        cacheRef.current.set(key, { value: parsed.value, fetchedAtMs });
        return cacheRef.current.get(key);
      } catch (_) {
        return null;
      }
    },
    [getStorageKey, isPersistableKey]
  );

  const getCacheEntry = useCallback((key) => {
    let entry = cacheRef.current.get(key);
    if (!entry) {
      entry = tryHydrateFromStorage(key) || null;
    }
    if (!entry) return { value: null, stale: true, ageMs: Infinity, fetchedAtMs: null };
    const ageMs = Date.now() - entry.fetchedAtMs;
    return {
      value: entry.value,
      stale: ageMs > STALE_TIME_MS,
      ageMs,
      fetchedAtMs: entry.fetchedAtMs,
    };
  }, [tryHydrateFromStorage]);

  const setCacheEntry = useCallback(
    (key, value, { fetchedAtMs = Date.now() } = {}) => {
      cacheRef.current.set(key, { value, fetchedAtMs });
      if (!isPersistableKey(key)) return;
      const storageKey = getStorageKey(key);
      if (!storageKey) return;
      if (typeof localStorage === 'undefined') return;
      try {
        localStorage.setItem(storageKey, JSON.stringify({ fetchedAtMs, value }));
      } catch (_) {
        // Ignore localStorage quota / privacy mode failures.
      }
    },
    [getStorageKey, isPersistableKey]
  );

  const invalidate = useCallback((key) => {
    cacheRef.current.delete(key);
    if (!isPersistableKey(key)) return;
    const storageKey = getStorageKey(key);
    if (!storageKey) return;
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.removeItem(storageKey);
    } catch (_) {
      // ignore
    }
  }, [getStorageKey, isPersistableKey]);

  const invalidatePrefix = useCallback((prefix) => {
    for (const key of cacheRef.current.keys()) {
      if (String(key).startsWith(prefix)) {
        cacheRef.current.delete(key);
      }
    }
    if (!currentUser?.uid) return;
    if (!isPersistableKey(prefix)) return;
    if (typeof localStorage === 'undefined') return;
    try {
      const prefixKey = `${STORAGE_PREFIX}:${currentUser.uid}:${prefix}`;
      for (let i = localStorage.length - 1; i >= 0; i -= 1) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefixKey)) {
          localStorage.removeItem(k);
        }
      }
    } catch (_) {
      // ignore
    }
  }, [currentUser, isPersistableKey]);

	  const authedFetchJson = useCallback(
	    async (url, options = {}) => {
	      if (!currentUser) {
	        throw new Error('Not authenticated');
	      }
	      const token = await currentUser.getIdToken();
	      const base = String(process.env.REACT_APP_API_BASE_URL || '').trim();
	      const resolvedUrl = (base && typeof url === 'string' && url.startsWith('/')) ? `${base}${url}` : url;
	      const headers = {
	        ...(options.headers || {}),
	        Authorization: `Bearer ${token}`,
	      };
	      const response = await fetch(resolvedUrl, { ...options, headers });
	      if (!response.ok) {
	        let errData = {};
	        try {
	          errData = (await safeReadJson(response, { context: resolvedUrl })) || {};
	        } catch (parseErr) {
	          // If server returned HTML/invalid JSON, surface a clearer message.
	          const err = new Error(parseErr.message || `Request failed (${response.status})`);
	          err.status = response.status;
	          err.preview = parseErr.preview;
	          throw err;
	        }
	        const err = new Error(errData.message || `Request failed (${response.status})`);
	        err.status = response.status;
	        throw err;
	      }
	      return (await safeReadJson(response, { context: resolvedUrl })) || {};
	    },
	    [currentUser]
	  );

  const fetchJsonCached = useCallback(
    async (cacheKey, url, { force = false, background = false } = {}) => {
      const entry = getCacheEntry(cacheKey);
      if (entry.value && !force && !entry.stale) {
        return { data: entry.value, fromCache: true, stale: false };
      }
      if (background && entry.value && !force && entry.stale) {
        authedFetchJson(url)
          .then((data) => setCacheEntry(cacheKey, data))
          .catch(() => {});
        return { data: entry.value, fromCache: true, stale: true };
      }
      const data = await authedFetchJson(url);
      setCacheEntry(cacheKey, data);
      return { data, fromCache: false, stale: false };
    },
    [authedFetchJson, getCacheEntry, setCacheEntry]
  );


  const getInventoryPieces = useCallback(
    async ({ force = false, background = false } = {}) => {
      const key = 'inventoryPieces:/api/v1/inventory/all-pieces';
      const entry = getCacheEntry(key);
      if (entry.value && !force && !entry.stale) {
        return { data: entry.value, fromCache: true, stale: false };
      }
      if (background && entry.value && !force && entry.stale) {
        authedFetchJson('/api/v1/inventory/all-pieces')
          .then((data) => setCacheEntry(key, data))
          .catch(() => {});
        return { data: entry.value, fromCache: true, stale: true };
      }
      const data = await authedFetchJson('/api/v1/inventory/all-pieces');
      setCacheEntry(key, data);
      return { data, fromCache: false, stale: false };
    },
    [authedFetchJson, getCacheEntry, setCacheEntry]
  );

  const getBundleDefinitions = useCallback(
    async ({ force = false, background = false } = {}) => {
      const key = 'bundles:/api/v1/bundles/all';
      const entry = getCacheEntry(key);
      if (entry.value && !force && !entry.stale) {
        return { data: entry.value, fromCache: true, stale: false };
      }
      if (background && entry.value && !force && entry.stale) {
        authedFetchJson('/api/v1/bundles/all')
          .then((data) => setCacheEntry(key, data))
          .catch(() => {});
        return { data: entry.value, fromCache: true, stale: true };
      }
      const data = await authedFetchJson('/api/v1/bundles/all');
      setCacheEntry(key, data);
      return { data, fromCache: false, stale: false };
    },
    [authedFetchJson, getCacheEntry, setCacheEntry]
  );

  const getFabricSpecifications = useCallback(
    async ({ force = false, background = false } = {}) => {
      const key = 'fabricSpecs:/api/v1/fabric-specifications/for-orders';
      const entry = getCacheEntry(key);
      if (entry.value && !force && !entry.stale) {
        return { data: entry.value, fromCache: true, stale: false };
      }
      if (background && entry.value && !force && entry.stale) {
        authedFetchJson('/api/v1/fabric-specifications/for-orders')
          .then((data) => setCacheEntry(key, data))
          .catch(() => {});
        return { data: entry.value, fromCache: true, stale: true };
      }
      const data = await authedFetchJson('/api/v1/fabric-specifications/for-orders');
      setCacheEntry(key, data);
      return { data, fromCache: false, stale: false };
    },
    [authedFetchJson, getCacheEntry, setCacheEntry]
  );

  const value = useMemo(
    () => ({
      getCacheEntry,
      setCacheEntry,
      invalidate,
      invalidatePrefix,
      fetchJsonCached,
      getInventoryPieces,
      getBundleDefinitions,
      getFabricSpecifications,
    }),
    [
      getCacheEntry,
      setCacheEntry,
      invalidate,
      invalidatePrefix,
      fetchJsonCached,
      getInventoryPieces,
      getBundleDefinitions,
      getFabricSpecifications,
    ]
  );

  return <DataCacheContext.Provider value={value}>{children}</DataCacheContext.Provider>;
}

export function useDataCache() {
  const ctx = useContext(DataCacheContext);
  if (!ctx) {
    throw new Error('useDataCache must be used within DataCacheProvider');
  }
  return ctx;
}
